import {useMemo} from 'react';
import {gql} from '@apollo/client';
import {
  faBookmark,
  faBurgerSoda,
  faCartShopping,
  faCircleNodes,
  faCreditCard,
  faGear,
  faMegaphone,
  faMemo,
  faStar,
  faStars,
  faStore,
  faThumbsUp,
  faTruck,
  type IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import {useBooleanAssignment} from '@ezcater/eppo-react';
import EppoFlags from '@src/EppoFlags';
import {NavQuery} from '@src/__generated__/NavQuery';
import useGuardedQuery from '@src/hooks/useGuardedQuery';
import {
  CLUB_SODA_PATH,
  DELIVERY_MANAGEMENT_DELIVERIES_PATH,
  INTEGRATION_CREDENTIALS_PATH,
  MARKETING_TOOLS_PATH,
  MENU_PATH,
  OPERATIONAL_METRICS_PATH,
  ORDER_INDEX_PATH,
  PAYMENTS_PATH,
  PERFORMANCE_PATH,
  PROMOTIONS_PATH,
  RANKINGS_PATH,
  RELIABILITY_ROCKSTAR_PATH,
  REVIEWS_PATH,
  SETTINGS_STORE_PATH,
  TRAINING_RESOURCES_PATH,
} from '@src/paths';
import {useTranslation} from 'react-i18next';
import {useEnvironment} from '@src/components/Environment';
import {useLocation} from 'react-router-dom';
import {NavItem, SubNavItem} from '../types';
import {
  buildOrdersSubnavItems,
  buildSettingsSubnavItems,
  buildMarketingToolsSubnavItems,
  buildDeliverySubNavItems,
  getActiveLink,
} from './subnavUtils';
import {useEzManageNavContext} from '../../EzManageNavContext';

const useNavItems = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const activeLink = location.pathname;
  const {appName: activeApp} = useEzManageNavContext();

  const showSelfServePromoTool = useBooleanAssignment(EppoFlags.SelfServePromoTool, false);
  const showDeliveryPerformanceReporting = useBooleanAssignment(
    EppoFlags.DeliveryPerformanceReporting,
    false
  );

  const DAILY_OPERATIONS = t('components.Navigation.sections.dailyOperations');
  const BUSINESS_INSIGHTS = t('components.Navigation.sections.businessInsights');
  const ENGAGE_CUSTOMERS = t('components.Navigation.sections.engageCustomers');
  const MANAGE_ACCOUNT = t('components.Navigation.sections.manageAccount');
  const SUPPORT_RESOURCES = t('components.Navigation.sections.supportResources');

  const NAV_SECTIONS = [
    DAILY_OPERATIONS,
    BUSINESS_INSIGHTS,
    ENGAGE_CUSTOMERS,
    MANAGE_ACCOUNT,
    SUPPORT_RESOURCES,
  ];

  const NAV_LINK_MAP = useMemo(
    () =>
      ({
        [ORDER_INDEX_PATH]: {section: DAILY_OPERATIONS, icon: faCartShopping},
        [DELIVERY_MANAGEMENT_DELIVERIES_PATH]: {section: DAILY_OPERATIONS, icon: faTruck},
        [CLUB_SODA_PATH]: {section: DAILY_OPERATIONS, icon: faBurgerSoda},
        [OPERATIONAL_METRICS_PATH]: {section: BUSINESS_INSIGHTS, icon: faStore},
        [RELIABILITY_ROCKSTAR_PATH]: {section: BUSINESS_INSIGHTS, icon: faStars},
        [REVIEWS_PATH]: {section: BUSINESS_INSIGHTS, icon: faThumbsUp},
        [PERFORMANCE_PATH]: {section: BUSINESS_INSIGHTS, icon: faStar},
        [MARKETING_TOOLS_PATH]: {section: ENGAGE_CUSTOMERS, icon: faMegaphone},
        [RANKINGS_PATH]: {section: ENGAGE_CUSTOMERS, icon: null},
        [PROMOTIONS_PATH]: {section: ENGAGE_CUSTOMERS, icon: null},
        [SETTINGS_STORE_PATH]: {section: MANAGE_ACCOUNT, icon: faGear},
        [PAYMENTS_PATH]: {section: MANAGE_ACCOUNT, icon: faCreditCard},
        [MENU_PATH]: {section: MANAGE_ACCOUNT, icon: faMemo},
        [INTEGRATION_CREDENTIALS_PATH]: {section: MANAGE_ACCOUNT, icon: faCircleNodes},
        [TRAINING_RESOURCES_PATH]: {section: SUPPORT_RESOURCES, icon: faBookmark},
      }) as Record<string, {section: string; icon: IconDefinition | null}>,
    [DAILY_OPERATIONS, BUSINESS_INSIGHTS, ENGAGE_CUSTOMERS, MANAGE_ACCOUNT, SUPPORT_RESOURCES]
  );

  const {appHost: currentAppRoot} = useEnvironment();

  const {data} = useGuardedQuery<NavQuery, object>(navQueryGql, {
    errorPolicy: 'ignore',
  });

  // orders nav permissions
  const canViewOrderIndexPage = data?.ezManagePermissions?.canViewOrderIndexPage ?? false;

  // settings nav permissions
  const canEditUserSettings = !!data?.ezManagePermissions?.canEditUserSettings;
  const canViewStoreSettingsPage = !!data?.ezManagePermissions?.canViewStoreSettingsPage;
  const canAccessPersonalStoreGroups = !!data?.me?.catererAccount?.canAccessPersonalStoreGroups;
  const canCreatePublicApiCredentials = !!data?.ezManagePermissions?.canCreatePublicApiCredentials;
  const settingsPermissions = {
    canEditUserSettings,
    canViewStoreSettingsPage,
    canAccessPersonalStoreGroups,
    canCreatePublicApiCredentials,
  };

  const catererAccount = data?.me?.catererAccount ?? null;

  const ezManageNavLinks = catererAccount?.ezManageNavLinks;

  const {t: useMessage} = useTranslation();

  const defaultHomeLabel = useMessage('components.EzManageNav.labels.defaultHome');

  const defaultHomeLink = {
    absoluteRoot: '',
    // the app will be the activeApp and we'll use the relative path
    appName: activeApp, // default to the current app while we load, so it will always be
    // a relative url
    relativePath: '#', // use "#" so the link doesn't go anywhere until it finishes loading
    httpMethod: 'GET',
    id: 'HOME',
    label: defaultHomeLabel,
  };

  const {
    homeLink = defaultHomeLink,
    mainNavLinks = [],
    accountNavLinks = [],
    utilityNavLinks = [],
  } = ezManageNavLinks || {};

  const homeNavItem = {
    key: homeLink.id,
    label: homeLink.label,
    to: homeLink.relativePath,
    isActive: homeLink.relativePath === activeLink,
    isLinkToActiveApp: false,
  };

  const buildSubnavItems = ({
    relativePath,
    absoluteRoot,
    isLinkToActiveApp,
  }: {
    relativePath: string;
    absoluteRoot: string;
    isLinkToActiveApp: boolean;
  }): Omit<SubNavItem, 'section'>[] => {
    switch (relativePath) {
      case '/settings/stores':
        return buildSettingsSubnavItems({
          absoluteRoot,
          isLinkToActiveApp,
          t,
          settingsPermissions,
          activeLink,
        });
      case '/orders':
        return buildOrdersSubnavItems({
          absoluteRoot,
          isLinkToActiveApp,
          t,
          activeLink,
        });
      case '/marketing-tools':
        return buildMarketingToolsSubnavItems({
          absoluteRoot,
          isLinkToActiveApp,
          t,
          catererAccount,
          ezManagePermissions: data?.ezManagePermissions ?? null,
          activeLink,
          showSelfServePromoTool,
        });
      case '/deliveries':
        return buildDeliverySubNavItems({
          absoluteRoot,
          isLinkToActiveApp,
          currentAppRoot,
          t,
          activeLink,
          showDeliveryPerformanceReporting,
        });
      default:
        return [];
    }
  };

  const mainNavLinksBySectionObj: Record<string, NavItem[]> = NAV_SECTIONS.reduce(
    (acc, section) => {
      acc[section] = [];
      return acc;
    },
    {} as Record<string, SubNavItem[]>
  );

  const links = [
    ...mainNavLinks.map(navLink => {
      const isLinkToActiveApp = navLink.appName === activeApp;
      const to = isLinkToActiveApp
        ? navLink.relativePath
        : `${navLink.absoluteRoot}${navLink.relativePath}`;

      return to;
    }),
    ...accountNavLinks
      .filter(link => link.id === 'PRIVACY_POLICY' || link.id === 'TERMS_AND_CONDITIONS')
      .map(navLink => {
        const to = `${navLink.absoluteRoot}${navLink.relativePath}`;
        return to;
      }),
  ];

  const activeNavLink = getActiveLink(activeLink, links);

  mainNavLinks.forEach(navLink => {
    const navLinkConfig = NAV_LINK_MAP[navLink.relativePath] || {section: '', icon: null};
    const isLinkToActiveApp = navLink.appName === activeApp;
    if (!canViewOrderIndexPage && navLink.relativePath === ORDER_INDEX_PATH) {
      return;
    }
    const to = isLinkToActiveApp
      ? navLink.relativePath
      : `${navLink.absoluteRoot}${navLink.relativePath}`;
    const isActive = activeNavLink === to;
    const item = {
      key: navLink.id,
      label: navLink.label,
      isLinkToActiveApp,
      to,
      subNavItems: buildSubnavItems({
        relativePath: navLink.relativePath,
        absoluteRoot: navLink.absoluteRoot,
        isLinkToActiveApp,
      }),
      isActive,
      section: navLinkConfig.section,
      icon: navLinkConfig.icon,
    };
    if (item.section in mainNavLinksBySectionObj) {
      mainNavLinksBySectionObj[item.section].push(item);
    }
  });

  const accountNavItems = accountNavLinks
    .filter(link => link.id === 'PRIVACY_POLICY' || link.id === 'TERMS_AND_CONDITIONS')
    .map(navLink => {
      const to = `${navLink.absoluteRoot}${navLink.relativePath}`;
      return {
        key: navLink.label,
        label: navLink.label,
        isLinkToActiveApp: false,
        to,
        isActive: activeNavLink === to,
      };
    });

  const logoutItem = accountNavLinks
    .filter(item => item.id === 'SIGN_OUT')
    .map(({absoluteRoot, relativePath, label}) => ({absoluteRoot, relativePath, label}))[0];

  const backToAdminItem = utilityNavLinks
    .filter(item => item.id === 'BACK_TO_ADMIN')
    .map(({absoluteRoot, relativePath, label}) => ({absoluteRoot, relativePath, label}))[0];

  const catererInfo = {
    name: catererAccount?.name ?? '',
    email: catererAccount?.email ?? '',
  };

  return {
    homeNavItem,
    mainNavLinksBySectionObj,
    footerProps: {accountNavItems, logoutItem, backToAdminItem, catererInfo},
  };
};

export default useNavItems;

export const navQueryGql = gql`
  query NavQuery {
    ezManagePermissions {
      id
      canViewDirectEntry
      canViewOrderIndexPage
      canEditUserSettings
      canViewStoreSettingsPage
      canViewRankingsIndexPage
      canManagePromos
      canCreatePublicApiCredentials
    }
    me {
      id
      catererAccount {
        id
        canAccessPersonalStoreGroups
        canViewSponsoredListings
        name
        email
        ezManageNavLinks {
          id
          homeLink {
            absoluteRoot
            appName
            id
            label
            httpMethod
            relativePath
          }
          mainNavLinks {
            absoluteRoot
            appName
            displayOnMobile
            id
            label
            notificationStar
            relativePath
          }
          accountNavLinks {
            absoluteRoot
            appName
            id
            label
            httpMethod
            relativePath
          }
          utilityNavLinks {
            absoluteRoot
            appName
            id
            label
            httpMethod
            relativePath
          }
        }
      }
    }
  }
`;
